// Zod schemas shared across auth forms (login, signup, OTP, password reset).
// The same schemas are used for AntD Form validation and as the parsing layer
// before hitting authService — guarantees the request body always matches the
// shape the backend expects.
import { z } from 'zod';
import { OTP_PURPOSE } from '@core/constants/auth';

const email = z.string().trim().toLowerCase().email('Enter a valid email');
const password = z.string().min(8, 'Password must be at least 8 characters');

export const mobileSchema = z
  .object({
    country_code: z.string().min(1).max(5).optional(),
    calling_code: z.string().min(1).max(5).optional(),
    number: z
      .string()
      .trim()
      .min(6, 'Enter a valid phone number')
      .max(15, 'Enter a valid phone number')
      .optional(),
  })
  .optional();

export const loginSchema = z
  .object({
    email: email.optional(),
    mobile: mobileSchema,
    password: z.string().min(1, 'Password is required'),
  })
  .refine((d) => Boolean(d.email) || Boolean(d.mobile?.number), {
    message: 'Email or phone is required',
    path: ['email'],
  });

export const signupSchema = z
  .object({
    first_name: z.string().trim().min(1, 'First name is required'),
    last_name: z.string().trim().optional(),
    email,
    password,
    confirmPassword: z.string(),
    mobile: mobileSchema,
    referral_code: z.string().trim().optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const verifyOtpSchema = z
  .object({
    email,
    otp: z
      .string()
      .trim()
      .regex(/^\d{4,6}$/, 'OTP must be 4–6 digits'),
    purpose: z.enum([OTP_PURPOSE.SIGNUP, OTP_PURPOSE.RESET_PASSWORD]),
    token: z.string().trim().optional(),
    password: password.optional(),
  })
  .refine((d) => d.purpose !== OTP_PURPOSE.RESET_PASSWORD || Boolean(d.password), {
    message: 'New password is required',
    path: ['password'],
  });

export const forgotPasswordSchema = z.object({
  email,
});

export const resendOtpSchema = z.object({
  email,
});
